/**
 * Admin auth (login / register). 라우트 admin.php auth 그룹과 동기화.
 * data-controller="AdminAuthController", data-action="login" | "handleLogin" | "showRegisterForm" | "register" | "logout"
 */

import { Form } from "../../core/Form";
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";

const FORM_SELECTOR = ".admin-auth__form";
const LOADER_SELECTOR = ".admin-auth__loader";

function getMessages(): Record<string, string> {
  const lang = document.documentElement.lang || "ko";
  const isKo = lang.startsWith("ko");
  return {
    emailRequired: isKo ? "이메일을 입력해주세요." : "Please enter your email.",
    passwordRequired: isKo ? "비밀번호를 입력해주세요." : "Please enter your password.",
    nameRequired: isKo ? "이름을 입력해주세요." : "Please enter your name.",
    passwordConfirmRequired: isKo ? "비밀번호 확인을 입력해주세요." : "Please confirm your password.",
    passwordMismatch: isKo ? "비밀번호가 일치하지 않습니다." : "Passwords do not match.",
    requestFailed: isKo ? "요청에 실패했습니다." : "Request failed.",
    errorOccurred: isKo ? "오류가 발생했습니다." : "An error occurred.",
  };
}

function validateLogin(form: HTMLFormElement): string | null {
  const messages = getMessages();
  const email = (form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? "").trim();
  const password = (form.querySelector<HTMLInputElement>('[name="password"]')?.value ?? "").trim();
  if (!email) return messages.emailRequired;
  if (!password) return messages.passwordRequired;
  return null;
}

function validateRegister(form: HTMLFormElement): string | null {
  const messages = getMessages();
  const name = (form.querySelector<HTMLInputElement>('[name="name"]')?.value ?? "").trim();
  const email = (form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? "").trim();
  const password = (form.querySelector<HTMLInputElement>('[name="password"]')?.value ?? "").trim();
  const confirm = (form.querySelector<HTMLInputElement>('[name="password_confirmation"]')?.value ?? "").trim();
  if (!name) return messages.nameRequired;
  if (!email) return messages.emailRequired;
  if (!password) return messages.passwordRequired;
  if (!confirm) return messages.passwordConfirmRequired;
  if (password !== confirm) return messages.passwordMismatch;
  return null;
}

function handleAuthSuccess(data: Record<string, unknown>): void {
  const msg = (data?.msg ?? data?.message ?? "") as string;
  const redirectUrl = (data?.redirect ?? data?.redirect_url) as string | undefined;
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }
  if (msg) void Modal.alert(msg);
}

export const AdminAuthController = {
  login(): void {
    Logger.info("AdminAuthController.login");
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return;

    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: validateLogin,
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: handleAuthSuccess,
      onFail: (message) => void Modal.alert(message),
    });
    f.init();
  },

  /** POST login 처리 후 리다이렉트 등; GET 페이지 없음 → no-op */
  handleLogin(): void {
    Logger.debug("AdminAuthController.handleLogin (no-op)");
  },

  showRegisterForm(): void {
    Logger.info("AdminAuthController.showRegisterForm");
    AdminAuthController.register();
  },

  register(): void {
    Logger.info("AdminAuthController.register");
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return;

    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: validateRegister,
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: handleAuthSuccess,
      onFail: (message) => void Modal.alert(message),
    });
    f.init();
  },

  /** POST logout → no-op (서버에서 처리) */
  logout(): void {
    Logger.debug("AdminAuthController.logout (no-op)");
  },
};
